import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/api/endpoints';
import Pagination from '@/components/common/Pagination';
import { Alert, Empty, ErrorState, Loading } from '@/components/common/State';
import { ORDER_STATUS_LABEL, ORDER_STATUS_TONE, formatDate, formatWon } from '@/utils/format';
import type { OrderStatus } from '@/api/types';

/**
 * 송장 입력 영역을 보여 줄 상태.
 * 결제 전이거나, 이미 출고됐거나, 취소 계열로 넘어간 주문은 배송을 시작할 수 없다.
 * 서버의 OrderStatus.isShippable() 과 짝을 이룬다(서버는 송장 정정을 위해 SHIPPING 도 허용).
 */
const SHIPPABLE_STATUSES: OrderStatus[] = ['PAID', 'PREPARING'];

export default function AdminOrderListPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [keyword, setKeyword] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'orders', page, status, keyword],
    queryFn: () =>
      adminApi.orders({ page, size: 20, status: status || undefined, keyword: keyword || undefined }),
  });

  const detail = useQuery({
    queryKey: ['admin', 'order', expanded],
    queryFn: () => adminApi.orderDetail(expanded!),
    enabled: expanded !== null,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'order', expanded] });
  };

  const prepare = useMutation({
    mutationFn: (orderId: number) => adminApi.prepare(orderId),
    onSuccess: () => {
      setFeedback('상품 준비중으로 변경했습니다.');
      invalidate();
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  const ship = useMutation({
    mutationFn: ({ orderId, courier, trackingNo }: { orderId: number; courier: string; trackingNo: string }) =>
      adminApi.ship(orderId, { courier, trackingNo }),
    onSuccess: () => {
      setFeedback('배송을 시작했습니다.');
      invalidate();
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  const deliver = useMutation({
    mutationFn: (orderId: number) => adminApi.deliver(orderId),
    onSuccess: () => {
      setFeedback('배송 완료로 변경했습니다.');
      invalidate();
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  const approveCancel = useMutation({
    mutationFn: (orderId: number) => adminApi.approveCancel(orderId),
    onSuccess: () => {
      setFeedback('취소를 확정했습니다. 결제 금액이 있으면 환불 대기로 넘어갑니다.');
      invalidate();
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  const completeRefund = useMutation({
    mutationFn: (orderId: number) => adminApi.completeRefund(orderId),
    onSuccess: () => {
      // 출고 이력이 있었는지에 따라 서버가 배송전/배송후 환불 완료로 갈라 준다.
      setFeedback('환불 완료로 처리했습니다.');
      invalidate();
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  return (
    <>
      <h1 className="panel-title">주문 · 배송 관리</h1>
      {feedback && <Alert tone="info">{feedback}</Alert>}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <select
          className="select"
          style={{ maxWidth: 180 }}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">전체 상태</option>
          {Object.entries(ORDER_STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <input
          className="input"
          style={{ maxWidth: 240 }}
          placeholder="주문번호 / 수령인 / 이메일"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setKeyword(e.currentTarget.value);
              setPage(1);
            }
          }}
        />
      </div>

      {isLoading && <Loading />}
      {isError && <ErrorState error={error} onRetry={refetch} />}

      {data &&
        (data.content.length === 0 ? (
          <Empty title="주문이 없습니다." />
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>주문번호</th>
                    <th>주문명</th>
                    <th>금액</th>
                    <th>상태</th>
                    <th>주문일시</th>
                    <th>처리</th>
                  </tr>
                </thead>
                <tbody>
                  {data.content.map((order) => (
                    <tr key={order.orderId}>
                      <td>{order.orderNo}</td>
                      <td className="wrap">{order.orderName}</td>
                      <td>{formatWon(order.paymentAmount)}</td>
                      <td>
                        <span className={`badge badge--${ORDER_STATUS_TONE[order.status]}`}>
                          {ORDER_STATUS_LABEL[order.status]}
                        </span>
                      </td>
                      <td>{formatDate(order.orderedAt, true)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {order.status === 'PAID' && (
                            <button
                              type="button"
                              className="btn btn--outline btn--sm"
                              onClick={() => prepare.mutate(order.orderId)}
                            >
                              준비중
                            </button>
                          )}
                          {order.status === 'SHIPPING' && (
                            <button
                              type="button"
                              className="btn btn--outline btn--sm"
                              onClick={() => deliver.mutate(order.orderId)}
                            >
                              배송완료
                            </button>
                          )}
                          {order.status === 'CANCEL_REQUESTED' && (
                            <button
                              type="button"
                              className="btn btn--outline btn--sm"
                              disabled={approveCancel.isPending}
                              onClick={() => approveCancel.mutate(order.orderId)}
                            >
                              취소완료
                            </button>
                          )}
                          {/* 취소가 확정된(canceledAt) 환불 대기 건만 환불 완료로 넘길 수 있다. */}
                          {order.status === 'REFUND_REQUESTED' && order.canceledAt && (
                            <button
                              type="button"
                              className="btn btn--outline btn--sm"
                              disabled={completeRefund.isPending}
                              onClick={() => completeRefund.mutate(order.orderId)}
                            >
                              환불완료
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => setExpanded(expanded === order.orderId ? null : order.orderId)}
                          >
                            상세
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {expanded !== null && (
              <div className="card" style={{ marginTop: 16 }}>
                <div className="card__body">
                  {detail.isLoading && <Loading />}
                  {detail.data && (
                    <>
                      <h2 style={{ fontSize: 16, marginTop: 0 }}>주문 {detail.data.orderNo}</h2>
                      <p className="muted">
                        {detail.data.userName} ({detail.data.userEmail}) · {detail.data.recipient} /{' '}
                        {detail.data.recipientPhone}
                        <br />[{detail.data.zipcode}] {detail.data.address1} {detail.data.address2}
                      </p>

                      <table className="table" style={{ marginBottom: 16 }}>
                        <thead>
                          <tr>
                            <th>상품</th>
                            <th>옵션</th>
                            <th>수량</th>
                            <th>금액</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.data.items.map((item) => (
                            <tr key={item.orderItemId}>
                              <td className="wrap">{item.productName}</td>
                              <td>{item.optionName}</td>
                              <td>{item.quantity}</td>
                              <td>{formatWon(item.totalPrice)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {SHIPPABLE_STATUSES.includes(detail.data.status) && (
                        <ShipForm
                          onSubmit={(courier, trackingNo) =>
                            ship.mutate({ orderId: detail.data!.orderId, courier, trackingNo })
                          }
                        />
                      )}
                      {detail.data.delivery?.trackingNo && (
                        <p>
                          운송장: <strong>{detail.data.delivery.courier}</strong>{' '}
                          {detail.data.delivery.trackingNo}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            <Pagination page={data.page} totalPages={data.totalPages} onChange={setPage} />
          </>
        ))}
    </>
  );
}

function ShipForm({ onSubmit }: { onSubmit: (courier: string, trackingNo: string) => void }) {
  const [courier, setCourier] = useState('CJ대한통운');
  const [trackingNo, setTrackingNo] = useState('');

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <input
        className="input"
        style={{ maxWidth: 160 }}
        value={courier}
        onChange={(e) => setCourier(e.target.value)}
        placeholder="택배사"
      />
      <input
        className="input"
        style={{ maxWidth: 200 }}
        value={trackingNo}
        onChange={(e) => setTrackingNo(e.target.value)}
        placeholder="송장번호"
      />
      <button
        type="button"
        className="btn btn--primary"
        disabled={!trackingNo}
        onClick={() => onSubmit(courier, trackingNo)}
      >
        배송 시작
      </button>
    </div>
  );
}
